<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">
            <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
              <div class="col p-0">
                <MerchantMultiselect :multiple="true" v-model="merchantsFilter" />
              </div>
            </div>
            <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
              <div class="col p-0">
                <StoreMultiselect :multiple="true" v-model="storesFilter" />
              </div>
            </div>
            <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
              <div class="col p-0">
                <TrafficGroupMultiselect :multiple="true" small v-model="trafficGroupsFilter" />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="requestDTO.fromDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.from_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="requestDTO.toDate" mode="dateTime" :model-modifiers="{string: true}" is24hr :masks="{modelValue: 'YYYY-MM-DD HH:mm:ss', inputDateTime24hr: 'YYYY-MM-DD HH:mm:ss'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.to_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>
            <div class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
              <div class="col p-0">
                <TraderMultiselect :multiple="true" v-model="tradersFilter" />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="methods"
                  class="multiselect-sm"
                  v-model="requestDTO.methods"
                  :options="store.getters['currencyMethods/methods']()"
                  :custom-label="(opt) => helper.trans(`pay_methods.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.payment_methods')"
                >
                  <template #option="props">
                    <div class="d-flex flex-row align-items-center gap-2">
                      <PayMethodIcon icon-class="icon" :method="props.option" />
                      <span class="overflow-hidden" style="white-space: nowrap; text-overflow: ellipsis">{{ helper.trans('pay_methods.' + props.option) }}</span>
                    </div>
                  </template>
                </Multiselect>
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="options"
                  class="multiselect-sm"
                  v-model="requestDTO.options"
                  :options="store.getters['currencyMethods/options']()"
                  :custom-label="(opt) => helper.trans(`payment_options.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.payment_options')"
                />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="currency"
                  class="multiselect-sm multiselect-capitalize"
                  v-model="requestDTO.currency"
                  :options="store.getters['currencyMethods/currencies']"
                  :custom-label="(opt) => opt"
                  :multiple="false"
                  select-label="Select"
                  :placeholder="helper.trans('labels.currency')"
                />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <input type="text" :disabled="!requestDTO.currency" v-model="requestDTO.amountFrom" class="form-control form-control-sm" :placeholder="helper.trans('labels.from_sum')" />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <input type="text" :disabled="!requestDTO.currency" v-model="requestDTO.amountTo" class="form-control form-control-sm" :placeholder="helper.trans('labels.to_sum')" />
              </div>
            </div>
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <Multiselect
                  id="types"
                  class="multiselect-sm"
                  v-model="requestDTO.types"
                  :options="Object.values(Direction)"
                  :custom-label="(opt) => helper.trans(`direction.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.type')"
                />
              </div>
            </div>
            <div class="form-group mb-2 col">
              <div class="col-auto p-0 text-end">
                <button type="button" @click.prevent="clearFilters" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!items.length" class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0 text-center text-muted py-3">
          {{ helper.trans('statistic.issuanceTime.no_data') }}
        </div>
      </div>
    </div>

    <div v-else class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative">
              <thead>
                <tr class="bg-light">
                  <th class="text-capitalize-first">{{ helper.trans('statistic.issuanceTime.hour') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.issuanceTime.count') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.issuanceTime.p50') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.issuanceTime.p90') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.issuanceTime.p98') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.issuanceTime.p99') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in items" :key="item.hour">
                  <td>{{ formatHour(item.hour) }}</td>
                  <td class="text-end">
                    {{ item.count }}
                    <i
                      v-if="item.count < 10"
                      v-b-tooltip.hover
                      class="fa fa-warning text-warning ms-1"
                      :title="helper.trans('statistic.issuanceTime.low_sample_warning')"
                    />
                  </td>
                  <td class="text-end" :class="{'text-danger': isOverThreshold(item.p50)}">{{ formatPercentile(item.p50) }}</td>
                  <td class="text-end" :class="{'text-danger': isOverThreshold(item.p90)}">{{ formatPercentile(item.p90) }}</td>
                  <td class="text-end" :class="{'text-danger': isOverThreshold(item.p98)}">{{ formatPercentile(item.p98) }}</td>
                  <td class="text-end" :class="{'text-danger': isOverThreshold(item.p99)}">{{ formatPercentile(item.p99) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </LoaderWrapper>
</template>
<script lang="ts">
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {defineComponent, onMounted, reactive, ref, toRefs, watch} from "vue";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {api} from "@/service/APIClient";
import {useStore} from "vuex";
import {debounce, helper} from "@/service/CommonHelper";
import {useNotification} from "@kyvg/vue3-notification";
import {DatePicker} from "v-calendar";
import Multiselect from "vue-multiselect";
import moment from "moment";
import MerchantMultiselect from "@/components/MerchantMultiselect.vue";
import StoreMultiselect from "@/components/StoreMultiselect.vue";
import TraderMultiselect from "@/components/TraderMultiselect.vue";
import TrafficGroupMultiselect from "@/components/TrafficGroupMultiselect.vue";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {Direction} from "@/models/enum/Direction";
import MerchantDTO from "@/models/MerchantDTO";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import TraderDTO from "@/models/TraderDTO";
import TrafficGroupDTO from "@/models/TrafficGroupDTO";
import IssuanceTimeStatItemDTO from "@/models/IssuanceTimeStatItemDTO";

const notification = useNotification();

function defaultRequestDTO() {
  return {
    fromDate: moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm:ss'),
    toDate: moment().format('YYYY-MM-DD HH:mm:ss'),
    methods: [] as string[],
    options: [] as string[],
    types: [] as string[],
    currency: null as string | null,
    amountFrom: null as string | null,
    amountTo: null as string | null,
  };
}

export default defineComponent({
  components: {
    LoaderWrapper,
    DatePicker,
    Multiselect,
    MerchantMultiselect,
    StoreMultiselect,
    TraderMultiselect,
    TrafficGroupMultiselect,
    PayMethodIcon,
  },
  setup() {
    const vuexStore = useStore();
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const skipFiltersWatch = ref(true);

    const state = reactive({
      merchantsFilter: [] as MerchantDTO[],
      storesFilter: [] as MerchantStoreDTO[],
      tradersFilter: [] as TraderDTO[],
      trafficGroupsFilter: [] as TrafficGroupDTO[],
      items: [] as IssuanceTimeStatItemDTO[],
      requestDTO: defaultRequestDTO(),
      alertCriticalMilliseconds: null as number | null,
    });

    const formatPercentile = (value: number | null) =>
      value == null ? '—' : `${numberFormatter.formatNumber(value, 3)} ${helper.trans('statistic.issuanceTime.milliseconds_suffix')}`;

    const formatHour = (hour: string) => dateHelper.format(hour, 'DD.MM.YYYY HH:mm');

    const isOverThreshold = (value: number | null) =>
      state.alertCriticalMilliseconds != null && value != null && value > state.alertCriticalMilliseconds;

    const syncAlertThreshold = async () => {
      state.alertCriticalMilliseconds = null;
      if (state.storesFilter.length !== 1) {
        return;
      }
      try {
        const storeDto = await api.storeGet(state.storesFilter[0].id);
        if (
          storeDto.issuanceTimeAlertEnabled
          && storeDto.issuanceTimeAlertCriticalMilliseconds != null
          && storeDto.issuanceTimeAlertCriticalMilliseconds > 0
        ) {
          state.alertCriticalMilliseconds = storeDto.issuanceTimeAlertCriticalMilliseconds;
        }
      } catch (e) {
      }
    };

    const buildParams = () => {
      const params: Record<string, unknown> = {
        fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
        toDate: dateHelper.toUTC(state.requestDTO.toDate),
      };
      const storeIds = state.storesFilter.map((s) => s.id);
      const merchantIds = state.merchantsFilter.map((m) => m.id);
      const traderIds = state.tradersFilter.map((t) => t.id);
      const trafficGroupsIds = state.trafficGroupsFilter.map((g) => g.id);
      if (storeIds.length) params.storeIds = storeIds;
      if (merchantIds.length) params.merchantIds = merchantIds;
      if (traderIds.length) params.traderIds = traderIds;
      if (trafficGroupsIds.length) params.trafficGroupsIds = trafficGroupsIds;
      if (state.requestDTO.methods?.length) params.methods = state.requestDTO.methods;
      if (state.requestDTO.options?.length) params.options = state.requestDTO.options;
      if (state.requestDTO.types?.length) params.types = state.requestDTO.types;
      if (state.requestDTO.currency) params.currency = state.requestDTO.currency;
      if (state.requestDTO.amountFrom) params.amountFrom = state.requestDTO.amountFrom;
      if (state.requestDTO.amountTo) params.amountTo = state.requestDTO.amountTo;
      return params;
    };

    const load = async () => {
      if (moment(state.requestDTO.toDate).diff(moment(state.requestDTO.fromDate), 'days', true) > 31) {
        notification.notify({
          type: 'error',
          title: helper.trans('labels.error'),
          text: helper.trans('statistic.issuanceTime.date_range_exceeded'),
        });
        return;
      }

      loader.value?.show();
      try {
        state.items = await api.statisticIssuanceTime(buildParams());
      } catch (error) {
        const err = error as { response?: { data?: { message?: string }[] }; message?: string };
        notification.notify({
          type: 'error',
          title: helper.trans('labels.error'),
          text: err?.response?.data?.[0]?.message ?? (error instanceof Error ? error.message : String(error)),
        });
      } finally {
        loader.value?.hide();
      }
    };

    const debouncedLoad = debounce(() => {
      load();
    }, 400);

    const clearFilters = () => {
      state.merchantsFilter = [];
      state.storesFilter = [];
      state.tradersFilter = [];
      state.trafficGroupsFilter = [];
      state.requestDTO = defaultRequestDTO();
      syncAlertThreshold();
      load();
    };

    watch(
      () => ({
        requestDTO: state.requestDTO,
        merchantsFilter: state.merchantsFilter,
        storesFilter: state.storesFilter,
        tradersFilter: state.tradersFilter,
        trafficGroupsFilter: state.trafficGroupsFilter,
      }),
      () => {
        if (skipFiltersWatch.value) {
          return;
        }
        syncAlertThreshold();
        debouncedLoad();
      },
      {deep: true},
    );

    onMounted(async () => {
      await syncAlertThreshold();
      await load();
      skipFiltersWatch.value = false;
    });

    return {
      ...toRefs(state),
      loader,
      helper,
      store: vuexStore,
      Direction,
      clearFilters,
      formatPercentile,
      formatHour,
      isOverThreshold,
    };
  },
});
</script>
